// src/pages/Clergy.jsx
import React from "react";
import terrenceImage from "../../assets/terrence.jpeg";
import clarenceImage from "../../assets/clarence.jpeg";
import tarsiImage from "../../assets/tarsi.jpeg";
import bishopImage from "../../assets/bishop.jpeg";

export default function Clergy() {
  const [expandedIndex, setExpandedIndex] = React.useState(null);

  const clergyMembers = [
    {
      name: "Fr. Terence Murray",
      title: "Parish Priest",
      image: terrenceImage,
      imagePosition: "object-[center_20%]",
      description: "Fr. Terence Murray has been serving as our Parish Priest, leading our community with wisdom, compassion, and unwavering dedication. His pastoral care and spiritual guidance have been instrumental in fostering a vibrant faith community at St. Stephen's. He brings years of pastoral experience and a deep commitment to serving the faithful."
    },
    {
      name: "Fr. Clarence Sequeira",
      title: "Associate Priest",
      image: clarenceImage,
      imagePosition: "object-cover",
      description: "Fr. Clarence Sequeira serves our parish community with joy and enthusiasm, bringing a warm and welcoming presence to all he encounters. His dedication to pastoral ministry and his ability to connect with parishioners of all ages makes him a beloved member of our clergy team. He is actively involved in various parish ministries and outreach programs."
    },
    {
      name: "Fr. Tarsicius Fernandes",
      title: "Priest in Residence",
      image: tarsiImage,
      imagePosition: "object-top",
      description: "Fr. Tarsicius Fernandes brings a wealth of experience and spiritual wisdom to our parish community. As a priest in residence, he enriches our spiritual life through his prayers, counsel, and active participation in parish activities. His gentle presence and deep faith inspire all who have the privilege of knowing him."
    },
    {
      name: "Bishop Savio Fernandes",
      title: "Bishop in Residence",
      image: bishopImage,
      imagePosition: "object-[center_30%]",
      scale: "scale-110",
      description: "Bishop Savio Fernandes graces our parish with his episcopal presence and profound spiritual leadership. His years of service to the Church and his pastoral wisdom provide invaluable guidance to our community. As Bishop in Residence, he offers his blessings, counsel, and support to both clergy and faithful alike."
    }
  ];

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 pt-20 pb-16">
      <style jsx>{`
        .gradient-title {
          display: inline-block;
          background: linear-gradient(90deg, #fb923c 0%, #facc15 48%, #fb923c 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          filter: drop-shadow(0 0 14px rgba(217,119,6,0.16));
          animation: gradient 3s ease infinite;
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .circular-avatar {
          width: 250px;
          height: 250px;
          border-radius: 50%;
          overflow: hidden;
          box-shadow: 
            0 10px 30px rgba(0, 0, 0, 0.15),
            0 20px 60px rgba(217, 119, 6, 0.2),
            0 0 0 8px rgba(255, 255, 255, 0.5),
            0 0 0 12px rgba(251, 191, 36, 0.3);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .circular-avatar:hover {
          transform: translateY(-8px) scale(1.05);
          box-shadow: 
            0 15px 40px rgba(0, 0, 0, 0.2),
            0 25px 70px rgba(217, 119, 6, 0.25),
            0 0 0 8px rgba(255, 255, 255, 0.6),
            0 0 0 12px rgba(251, 191, 36, 0.4);
        }

        .description-card {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.5s ease-in-out, opacity 0.5s ease-in-out, margin-top 0.5s ease-in-out;
          opacity: 0;
        }

        .description-card.expanded {
          max-height: 500px;
          opacity: 1;
          margin-top: 2rem;
        }

        @media (max-width: 768px) {
          .circular-avatar {
            width: 200px;
            height: 200px;
          }
        }
      `}</style>

      {/* Header Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mb-12">
        <div className="text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="gradient-title">Our Clergy</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Meet the dedicated priests and bishop who shepherd our parish community with faith, wisdom, and compassion.
          </p>
        </div>
      </section>

      {/* Clergy Profiles */}
      <section className="max-w-7xl mx-auto px-4 md:px-6">
        {/* First Row - 3 Priests */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-12 mb-12">
          {clergyMembers.slice(0, 3).map((member, index) => (
            <div key={index} className="flex flex-col items-center">
              {/* Circular Avatar and Name */}
              <div 
                className="flex flex-col items-center cursor-pointer"
                onClick={() => toggleExpand(index)}
              >
                <div className="circular-avatar bg-gradient-to-br from-amber-100/60 to-orange-100/60">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className={`w-full h-full object-cover ${member.imagePosition} ${member.scale || ''}`}
                  />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-6 hover:text-amber-600 transition-colors text-center">
                  {member.name}
                </h2>
                <p className="text-amber-600 font-semibold text-base md:text-lg mt-1">
                  {member.title}
                </p>
              </div>

              {/* Expandable Description */}
              <div className={`description-card w-full ${expandedIndex === index ? 'expanded' : ''}`}>
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-lg border border-amber-200/50">
                  <p className="text-gray-700 leading-relaxed text-center md:text-left">
                    {member.description}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(index);
                    }}
                    className="mt-4 mx-auto block text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-2 transition-colors"
                  >
                    <span>Close</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="rotate-180"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Second Row - Bishop Centered */}
        <div className="flex justify-center">
          <div className="w-full md:w-1/3 flex flex-col items-center">
            {(() => {
              const member = clergyMembers[3];
              const index = 3;
              return (
                <>
                  {/* Circular Avatar and Name */}
                  <div 
                    className="flex flex-col items-center cursor-pointer"
                    onClick={() => toggleExpand(index)}
                  >
                    <div className="circular-avatar bg-gradient-to-br from-amber-100/60 to-orange-100/60">
                      <img 
                        src={member.image}
                        alt={member.name}
                        className={`w-full h-full object-cover ${member.imagePosition} ${member.scale || ''}`}
                      />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-6 hover:text-amber-600 transition-colors text-center">
                      {member.name}
                    </h2>
                    <p className="text-amber-600 font-semibold text-base md:text-lg mt-1">
                      {member.title}
                    </p>
                  </div>

                  {/* Expandable Description */}
                  <div className={`description-card w-full ${expandedIndex === index ? 'expanded' : ''}`}>
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-lg border border-amber-200/50">
                      <p className="text-gray-700 leading-relaxed text-center md:text-left">
                        {member.description}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(index);
                        }}
                        className="mt-4 mx-auto block text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-2 transition-colors"
                      >
                        <span>Close</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="rotate-180"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </section>

      {/* Decorative Background Elements */}
      <div className="fixed top-20 right-10 w-72 h-72 bg-gradient-to-bl from-amber-400/10 to-orange-300/10 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="fixed bottom-20 left-10 w-72 h-72 bg-gradient-to-tr from-yellow-400/10 to-amber-300/10 rounded-full blur-3xl pointer-events-none -z-10" />
    </main>
  );
}